"use client"

import { useEffect, useState } from "react"
import { useLocale } from "next-intl"
import { MODAL_SLOT_MAP, type ModalContentRow } from "@/lib/modal-content-defs"

type AllContent = Record<string, ModalContentRow>

let cache: AllContent | null = null
let inflight: Promise<AllContent> | null = null

// One request serves every modal on the page. It is capped at 1.5s: a slow or
// failed read must never hold a modal (the 18+ gate included) hostage, so the
// modal just falls back to its built-in art and copy.
export function loadModalContent(): Promise<AllContent> {
  if (cache) return Promise.resolve(cache)
  if (inflight) return inflight
  const request: Promise<AllContent | null> = fetch("/api/modal-content")
    .then((r) => (r.ok ? (r.json() as Promise<AllContent>) : null))
    .catch(() => null)
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500))
  inflight = Promise.race([request, timeout]).then((result) => {
    inflight = null
    if (result) cache = result
    return result ?? {}
  })
  return inflight
}

/** Call after an admin saves so the next read is fresh. */
export function invalidateModalContent() {
  cache = null
  inflight = null
}

function fillTokens(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (whole, name) => (name in vars ? String(vars[name]) : whole))
}

/**
 * Admin-editable art and copy for a pop-up modal (see lib/modal-content-defs.ts).
 *
 * `ready` flips true once the read has finished (or timed out); a modal should
 * wait for it before painting, otherwise the built-in art flashes up and is
 * then swapped for the override. `art(key)` gives the override or the built-in
 * image. `text(key, fallback, vars)` gives the override (English only, since
 * overrides are written in English) or `fallback`, which is the modal's own
 * translated string. `{name}` tokens in an override are filled from `vars`.
 */
export function useModalContent(slot: string, enabled = true) {
  const locale = useLocale()
  const [row, setRow] = useState<ModalContentRow | null | undefined>(undefined)

  // `enabled` lets an always-mounted modal (Founders, early access, the age
  // gate) skip the request until it is actually about to show, so ordinary
  // page loads do not each pay for it.
  useEffect(() => {
    if (!enabled) return
    let alive = true
    loadModalContent().then((all) => {
      if (alive) setRow(all[slot] ?? null)
    })
    return () => {
      alive = false
    }
  }, [slot, enabled])

  const def = MODAL_SLOT_MAP[slot]

  return {
    ready: row !== undefined,
    art: (key: string): string => row?.art?.[key] || def?.art.find((a) => a.key === key)?.defaultUrl || "",
    /** True when an admin uploaded their own art for this key (so a card that rotates its built-in pictures should show the upload instead). */
    hasArtOverride: (key: string): boolean => !!row?.art?.[key],
    text: (key: string, fallback: string, vars?: Record<string, string | number>): string => {
      const override = locale === "en" ? row?.text?.[key] : undefined
      return override ? fillTokens(override, vars) : fallback
    },
  }
}
